import { createContext, useContext } from 'react'

import { IProjectClosedInterval } from '~/client/graph'
import { ICompanyMetadata } from '~/client/src/shared/components/CompanyListSelector'
import Company from '~/client/src/shared/models/Company'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import { ResourceControlsDraftApi } from './useResourceControlsDraft'

/**
 * What the Controls tab content and the (sibling, pinned) Cancel/Save footer both
 * read: the draft API plus the store-derived display data the editor cells need.
 * Provided by ResourceControlsTabProvider, which wraps the panel body so the footer
 * — rendered outside the tab's scroll area — can drive Save/Cancel.
 */
export interface ResourceControlsTabContextValue extends ResourceControlsDraftApi {
  recordId: string
  /** The resource family this record belongs to, for controls that list its fields. */
  resourceTypeId: string
  /** Users who may not change the resource (canEditResource) see the controls but can't edit them or Save. */
  readOnly: boolean
  companies: Company[]
  companyMetadata?: Map<string, ICompanyMetadata>
  getCompanyById: (id: string) => Company | undefined
  getPermitTypeById: (id: string) => WorkflowType | undefined
  allWorkflowTypeIds: string[]
  projectDateStore: ProjectDateStore
  /**
   * Navigates to the project settings where working hours are edited; drives the
   * inherited-hours chip's "Project Settings" link. Undefined for non-admins
   * (project settings are admin-only) and where navigation isn't available (e.g. mobile), leaving it
   * inactive.
   */
  onEditProjectHours?: () => void
  /** Mobile surfaces point the inherited-hours chip to the desktop version. */
  isMobile?: boolean
  /** The project's closures, when this record inherits them (the site root). */
  inheritedClosures?: IProjectClosedInterval[]
}

export const ResourceControlsTabContext =
  createContext<ResourceControlsTabContextValue | null>(null)

/** Footer reads this; null when the active resource has no Controls tab. */
export function useResourceControlsTabContext(): ResourceControlsTabContextValue | null {
  return useContext(ResourceControlsTabContext)
}

/** Tab content requires the provider. */
export function useResourceControlsTab(): ResourceControlsTabContextValue {
  const context = useContext(ResourceControlsTabContext)
  if (!context) {
    throw new Error(
      'useResourceControlsTab must be used within a ResourceControlsTabProvider',
    )
  }
  return context
}
